import { FormRow } from '@creditornot/cb-form';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Item, ProductHierarchyLevel } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { isDefined } from 'common/utils';
import { isAgeRestriction, isOTCRestriction } from 'modules/menu/utils';

type Props = {
  item: Item;
  productHierarchies: ProductHierarchyLevel[];
  alcoholWarningPermilleLimit: number;
};

const Row = styled.div`
  margin-block-start: 12px;
`;

const ItemRestrictions = ({ item, productHierarchies, alcoholWarningPermilleLimit }: Props) => {
  const intl = useIntl();

  const itemHierarchy = useMemo(
    () => productHierarchies.find(level => level.id === item.product_hierarchy_level_id),
    [item.product_hierarchy_level_id, productHierarchies],
  );

  const isTobacco = itemHierarchy?.tags.includes('tobacco');
  const isAlcohol = alcoholWarningPermilleLimit !== undefined && item.alcohol_percentage > alcoholWarningPermilleLimit;

  const isRestrictedItem = isTobacco || isAlcohol || item.restrictions.length > 0;

  return (
    <>
      <FormRow label={intl.formatMessage({ id: 'menu-item.restricted-item' })}>
        {isRestrictedItem
          ? intl.formatMessage({ id: 'menu-item.restricted-item-yes' })
          : intl.formatMessage({ id: 'menu-item.restricted-item-no' })}
      </FormRow>

      {isRestrictedItem && (
        <FormRow
          style={{
            borderTop: 'none',
          }}
          label={intl.formatMessage({ id: 'menu-item.restriction-type' })}
        >
          {[
            isAlcohol ? intl.formatMessage({ id: 'menu-item.restricted-product.alcohol' }) : undefined,
            isTobacco ? intl.formatMessage({ id: 'menu-item.restricted-product.tobacco' }) : undefined,
            item.restrictions.find(isOTCRestriction)
              ? intl.formatMessage({ id: 'menu-item.over-the-counter-medicine' })
              : undefined,
            item.restrictions.find(isAgeRestriction)
              ? intl.formatMessage({ id: 'menu-item.age-restricted-item' })
              : undefined,
            item.restrictions.find(({ type }) => type === 'delivery_handshake')
              ? intl.formatMessage({ id: 'menu-item.delivery-handshake' })
              : undefined,
          ]
            .filter(isDefined)
            .join(', ')}

          {!!item.restrictions.find(isAgeRestriction)?.age_limit && (
            <Row>
              <FormattedMessage id="menu-item.age-limit" />: {item.restrictions.find(isAgeRestriction)?.age_limit}
            </Row>
          )}
        </FormRow>
      )}
    </>
  );
};

export default ItemRestrictions;
